import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, Loader2, Download, ArrowRight, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      
      // Get order ID from URL params or localStorage (for Chapa redirects)
      const orderIdToFetch = orderId || localStorage.getItem('pending_order_id');
      
      if (!orderIdToFetch) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      console.log('📦 Fetching order details for:', orderIdToFetch);

      const response = await axios.get(`${BACKEND_URL}/orders/${orderIdToFetch}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setOrder(response.data);
      
      // Clear pending order ID from localStorage
      localStorage.removeItem('pending_order_id');
      
      console.log('✅ Order details loaded:', response.data);
    } catch (err) {
      console.error('❌ Error fetching order:', err);
      setError(err.response?.data?.detail || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      console.log('📄 Downloading invoice for order:', order.id);
      // TODO: Implement invoice download
      alert('Invoice download functionality coming soon!');
    } catch (err) {
      console.error('❌ Error downloading invoice:', err);
      alert('Failed to download invoice');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="text-center p-12">
          <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading order details...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-600">Order Not Found</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/my-add-ons')} className="mt-4">
              View My Add-ons
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isCompleted = order.status === 'completed' || order.payment_status === 'completed';
  const isPending = order.status === 'pending_payment' || order.payment_status === 'pending';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Success/Pending Header */}
      <Card className="text-center">
        <CardHeader>
          <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
            isCompleted ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            {isCompleted ? (
              <CheckCircle className="h-8 w-8 text-green-600" />
            ) : (
              <Loader2 className="h-8 w-8 text-yellow-600 animate-spin" />
            )}
          </div>
          <CardTitle className={`text-2xl ${isCompleted ? 'text-green-600' : 'text-yellow-600'}`}>
            {isCompleted ? 'Order Complete!' : 'Payment Pending'}
          </CardTitle>
          <CardDescription>
            {isCompleted 
              ? 'Thank you for your purchase. Your order has been processed successfully.' 
              : 'Your order is being processed. You will receive a confirmation once payment is verified.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Order Details */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-left">
                <div className="text-gray-600">Order Number:</div>
                <div className="font-semibold">{order.id}</div>
              </div>
              <div className="text-right">
                <div className="text-gray-600">Order Date:</div>
                <div className="font-semibold">
                  {new Date(order.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="text-left">
                <div className="text-gray-600">Payment Method:</div>
                <div className="font-semibold capitalize">{order.payment_method}</div>
              </div>
              <div className="text-right">
                <div className="text-gray-600">Total Amount:</div>
                <div className="font-semibold text-lg">${order.total_amount?.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Items Purchased */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3 flex items-center justify-center">
              <Package className="h-5 w-5 mr-2" />
              Items Purchased
            </h3>
            <div className="space-y-2">
              {order.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div className="text-left">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-600">
                      {item.item_type === 'add_on' ? 'Add-on Service' : 'Subscription'}
                      {item.quantity > 1 && ` × ${item.quantity}`}
                    </div>
                  </div>
                  <div className="text-right font-semibold">
                    ${item.total_price?.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={downloadInvoice}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Invoice
            </Button>
            <Button 
              onClick={() => navigate('/my-add-ons')}
              className="flex-1"
            >
              View My Add-ons
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Activation Notice */}
          {isCompleted && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium">✓ Your add-ons have been activated!</p>
              <p className="mt-1">You can now access all purchased features in your dashboard.</p>
            </div>
          )}

          {isPending && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              <p className="font-medium">⏳ Payment verification in progress</p>
              <p className="mt-1">Your add-ons will be activated once payment is confirmed. This usually takes a few minutes.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderConfirmation;
